import { Skeleton, useMediaQuery } from "@mui/material";
import { FC, RefObject, useRef, useState } from "react";
import UnumSync from "../../../assets/icons/UnumSync.svg";
import BurgerMenu from "../../../assets/icons/UnumSync/menu-01.svg";
import CloseBurger from "../../../assets/icons/UnumSync/x-close.svg";
import {
  StyledBottomSection,
  StyledClosePannel,
  StyledHeader,
  StyledIconButton,
  StyledNav,
} from "./NestedNavbar.styles";
import FadeScroll from "../../Effects/FadeScroll/FadeScroll";
import NavLink from "./NavItem/NavLink/NavLink";
import NavItem from "./NavItem/NavItem";
import { navBar, navItem } from "types/CMS/Unum/Navbar/navbar";
import { isNavLink } from "types/CMS/Unum/Navbar/navLink";
import useGetUser from "hooks/useGetUser";
import { roleCheck } from "utils/User/Roles";

type navBarProps = {
  navBar: navBar;
  onLogout: () => void;
  onClick: (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    navItem: navItem,
  ) => void;
  activeLink?: string;
  isLoading: boolean;
};

const Navbar: FC<navBarProps> = ({
  navBar,
  onLogout,
  onClick,
  activeLink,
  isLoading,
}) => {
  const isMobile = !useMediaQuery("(min-width:768px)");
  const [isOpen, setIsOpen] = useState(false);
  const listWrapperRef = useRef<HTMLElement>(null);
  const listParentRef = useRef<HTMLElement>(null);

  const navOnClick = (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
    navItem: navItem,
  ) => {
    // for mobile, close navbar on link click
    if (isNavLink(navItem)) {
      setIsOpen(false);
    }
    return onClick(e, navItem);
  };

  const { user } = useGetUser();

  return (
    <>
      <StyledHeader>
        {isMobile && !isOpen && (
          <StyledIconButton
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Open navigation menu"
            aria-expanded={isOpen}
            aria-controls="navbar"
            $IconColor="#364152"
          >
            <BurgerMenu />
          </StyledIconButton>
        )}
        <UnumSync width="153px" height="32px" />
      </StyledHeader>
      <StyledNav $isOpen={isOpen || !isMobile} id={`navbar`}>
        <div
          style={{
            overflowY: "auto",
            flexGrow: 1,
            whiteSpace: "nowrap",
          }}
          ref={listParentRef as RefObject<HTMLDivElement>}
        >
          {isLoading && (
            <>
              {[...Array(5)].map((_, index) => (
                <Skeleton
                  key={`skeleton-${_}-${index}`}
                  sx={{
                    height: 67,
                    margin: "-12px 8px",
                    borderRadius: "4px",
                  }}
                />
              ))}
            </>
          )}
          {!isLoading && (
            <ul ref={listWrapperRef as RefObject<HTMLUListElement>}>
              {navBar.items.map((item) => (
                <NavItem
                  activeLink={activeLink}
                  key={item.id}
                  item={item}
                  depth={0}
                  onClick={navOnClick}
                />
              ))}
              <FadeScroll
                refChild={listWrapperRef}
                refScrollContainer={listParentRef}
              />
            </ul>
          )}
        </div>

        <StyledBottomSection>
          <ul>
            {roleCheck(["admin"], user.role) && (
              <NavLink
                onClick={navOnClick}
                activeLink={activeLink}
                item={{
                  id: "userManagement-link",
                  type: "internal",
                  label: "Manage user access",
                  icon: "Settings01",
                  link: "/usermanagement",
                  depth: 0,
                }}
              />
            )}
            <NavLink
              onClick={() => {
                onLogout();
                return false;
              }}
              item={{
                id: "logout-link",
                type: "internal",
                label: "Log out",
                icon: "LogOut01",
                link: "/logout",
                depth: 0,
              }}
            />
          </ul>
        </StyledBottomSection>

        {isMobile && isOpen && (
          <StyledClosePannel $isOpen={isOpen}>
            <StyledIconButton
              aria-label="Close navigation menu"
              aria-expanded={isOpen}
              aria-controls={`navbar`}
              onClick={() => {
                setIsOpen(!isOpen);
              }}
            >
              <CloseBurger height="24px" width="24px" />
            </StyledIconButton>
          </StyledClosePannel>
        )}
      </StyledNav>
    </>
  );
};

export default Navbar;
